import { Box, Container, Typography, Paper, Grid, IconButton, Button, Card, CardContent, Chip, Divider, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DiamondIcon from '@mui/icons-material/Diamond';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import masterService from '../services/masterService';
import { useAuth } from '../context/AuthContext';

// Helper function to get icon and color based on plan name
const getPlanIconAndColor = (planName) => {
  if (planName.includes('Ittadi Bindhi')) {
    return {
      icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
      color: '#FF6B9D'
    };
  } else if (planName.includes('Vendi Bindhi')) {
    return {
      icon: <DiamondIcon sx={{ fontSize: 32 }} />,
      color: '#4ECDC4'
    };
  } else if (planName.includes('Bangaram Bindhi')) {
    return {
      icon: <StarIcon sx={{ fontSize: 32 }} />,
      color: '#FFD93D'
    };
  } else {
    return {
      icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
      color: '#FF6B9D'
    };
  }
};

const Purchase = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly'); // 'monthly', 'quarterly'

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const plansData = await masterService.getPlans();
        setPlans(plansData);
      } catch (err) {
        setError('Failed to fetch plans. Please try again later.');
        console.error('Error fetching plans:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePurchase = async (plan) => {
    if (!currentUser) {
      alert('Please login to purchase a plan.');
      navigate('/login-page');
      return;
    }

    try {
      setProcessingPayment(true);
      const payload = {
        purpose: `Plan Subscription - ${plan.plan_name}`,
        amount: parseFloat(plan.price),
        name: currentUser.first_name + " " + currentUser.last_name,
        email: currentUser.email,
        phone: currentUser.mobile,
        user_unique_id: currentUser.unique_id,
        plan_id: plan.plan_id,
        redirect_type: "web",
      };
      const response = await masterService.createPayment(payload);
      if (response.status === "success") {
        window.location.href = response.payment_url; // redirect to Instamojo
      } else {
        alert("Failed to create payment.");
      }
    } catch (error) {
      console.error("Payment Error:", error);
      alert("Error creating payment.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const groupPlansByBillingCycle = (plans) => {
    const grouped = {};
    plans.forEach(plan => {
      let cycle = plan.billing_cycle;
      
      // Infer billing cycle if it's empty based on plan_name
      if (!cycle || cycle === "") {
        if (plan.plan_name.includes('3 Months')) {
          cycle = 'quarterly';
        } else {
          cycle = 'monthly'; // Default for plans without explicit cycle in name
        }
      }
      
      if (!grouped[cycle]) {
        grouped[cycle] = [];
      }
      grouped[cycle].push(plan);
    });
    console.log('Grouped plans:', grouped);
    return grouped;
  };

  const getBillingCycleDisplayName = (cycle) => {
    switch (cycle) {
      case 'monthly':
        return 'Monthly';
      case 'quarterly':
        return 'Quarterly';
      default:
        return cycle;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: theme.palette.background.default 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: theme.palette.background.default 
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error" mb={2}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: theme.palette.background.default,
      background: `linear-gradient(135deg, ${theme.palette.primary.light}05 0%, ${theme.palette.primary.light}15 100%)`,
      py: 4, 
      position: 'relative' 
    }}>
      {/* Navigation Buttons */}
      <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10, display: 'flex', gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/')} 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.95)', 
            '&:hover': { bgcolor: theme.palette.primary.light, transform: 'scale(1.1)' }, 
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
            transition: 'all 0.3s' 
          }}
        >
          <HomeIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.95)', 
            '&:hover': { bgcolor: theme.palette.primary.light, transform: 'scale(1.1)' }, 
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
            transition: 'all 0.3s' 
          }}
        >
          <ArrowBackIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, mt: 8 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            fontWeight={800} 
            color={theme.palette.primary.main} 
            mb={2}
            sx={{ 
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 2
            }}
          >
            Choose Your Perfect Plan
          </Typography>
          <Typography variant="h6" color={theme.palette.text.secondary} sx={{ fontWeight: 400 }}>
            Unlock the power of meaningful connections with our premium packages
          </Typography>
        </Box>

        {/* Billing Cycle Selector */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ButtonGroup 
            selectedBillingCycle={selectedBillingCycle} 
            setSelectedBillingCycle={setSelectedBillingCycle} 
            theme={theme}
          />
          
        </Box>

        {/* Pricing Cards - Grouped by Billing Cycle */}
        {(() => {
          const groupedPlans = groupPlansByBillingCycle(plans);
          const currentPlans = groupedPlans[selectedBillingCycle] || [];
          
          if (currentPlans.length === 0) {
            return (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No plans available for this billing cycle
                </Typography>
              </Box>
            );
          }

          return (
            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
              {currentPlans.map((plan, idx) => {
                const { icon, color } = getPlanIconAndColor(plan.plan_name);
                const isFeatured = plan.plan_name.includes('Vendi Bindhi') && selectedBillingCycle === '6-months';
                const savings = selectedBillingCycle !== 'monthly' ? `Save up to ${selectedBillingCycle === 'yearly' ? '25%' : selectedBillingCycle === '6-months' ? '15%' : '10%'}` : null;
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3.5} key={plan.plan_id} sx={{ 
                    pt: isFeatured ? 2 : 0,
                    pb: isFeatured ? 2 : 0
                  }}>
                    <Card sx={{ 
                      height: '100%',
                      borderRadius: 3, 
                      boxShadow: isFeatured 
                        ? '0 16px 48px rgba(0,0,0,0.2)' 
                        : '0 4px 20px rgba(0,0,0,0.08)',
                      border: isFeatured 
                        ? `4px solid ${color}` 
                        : `2px solid ${theme.palette.primary.light}30`,
                      bgcolor: isFeatured 
                        ? `${color}12` 
                        : 'white',
                      position: 'relative',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      transform: isFeatured ? 'scale(1.02)' : 'scale(1)',
                      overflow: 'visible',
                      '&:hover': {
                        transform: isFeatured ? 'scale(1.05) translateY(-8px)' : 'scale(1.03) translateY(-5px)',
                        boxShadow: isFeatured 
                          ? '0 24px 64px rgba(0,0,0,0.3)' 
                          : '0 12px 40px rgba(0,0,0,0.15)'
                      }
                    }}>
                      {/* Featured Badge */}
                      {isFeatured && (
                        <Box sx={{
                          position: 'absolute',
                          top: 2,
                          right: 0,
                          zIndex: 2
                        }}>
                          <Chip 
                            label="MOST POPULAR" 
                            sx={{ 
                              bgcolor: color, 
                              color: 'white',
                              fontWeight: 700, 
                              fontSize: '0.7rem',
                              px: 2,
                              py: 1,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              '& .MuiChip-label': { px: 1 }
                            }} 
                          />
                        </Box>
                      )}

                      {/* Savings Badge */}
                      {savings && (
                        <Box sx={{
                          position: 'absolute',
                          top: 2,
                          left: 0,
                          zIndex: 2
                        }}>
                          <Chip 
                            label={savings} 
                            sx={{ 
                              bgcolor: '#4CAF50', 
                              color: 'white',
                              fontWeight: 700, 
                              fontSize: '0.7rem',
                              px: 2,
                              py: 1,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              '& .MuiChip-label': { px: 1 }
                            }} 
                          />
                        </Box>
                      )}

                      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Icon and Name */}
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: color,
                            color: 'white',
                            mx: 'auto',
                            mb: 2,
                            boxShadow: `0 8px 24px ${color}40`
                          }}>
                            {icon}
                          </Box>
                          <Typography variant="h5" fontWeight={700} color={theme.palette.text.primary} mb={1}>
                            {plan.plan_name}
                          </Typography>
                          <Typography variant="h4" fontWeight={900} color={color} mb={1}>
                            ₹{parseFloat(plan.price).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color={theme.palette.text.secondary} sx={{ fontWeight: 500 }}>
                            {getBillingCycleDisplayName(selectedBillingCycle)}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2, opacity: 0.3 }} />

                        {/* Features List */}
                        <Box sx={{ flex: 1, mb: 3 }}>
                          <Box component="ul" sx={{ 
                            listStyle: 'none', 
                            p: 0, 
                            m: 0,
                            textAlign: 'left'
                          }}>
                            <li style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              marginBottom: 12 
                            }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  color: color, 
                                  fontSize: 20, 
                                  mr: 1.5, 
                                  mt: 0.2,
                                  flexShrink: 0
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={theme.palette.text.secondary} 
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.4
                                }}
                              >
                                Search: {plan.search_fields} Fields
                              </Typography>
                            </li>
                            <li style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              marginBottom: 12 
                            }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  color: color, 
                                  fontSize: 20, 
                                  mr: 1.5, 
                                  mt: 0.2,
                                  flexShrink: 0
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={theme.palette.text.secondary} 
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.4
                                }}
                              >
                                Mutual Interests: {plan.mutual_interests}
                              </Typography>
                            </li>
                            <li style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              marginBottom: 12 
                            }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  color: color, 
                                  fontSize: 20, 
                                  mr: 1.5, 
                                  mt: 0.2,
                                  flexShrink: 0
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={theme.palette.text.secondary} 
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.4
                                }}
                              >
                                Chat: {plan.chat_requests}
                              </Typography>
                            </li>
                            <li style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              marginBottom: 12 
                            }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  color: color, 
                                  fontSize: 20, 
                                  mr: 1.5, 
                                  mt: 0.2,
                                  flexShrink: 0
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={theme.palette.text.secondary} 
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.4
                                }}
                              >
                                Express Interest: {plan.express_interest}
                              </Typography>
                            </li>
                            <li style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              marginBottom: 12 
                            }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  color: color, 
                                  fontSize: 20, 
                                  mr: 1.5, 
                                  mt: 0.2,
                                  flexShrink: 0
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={theme.palette.text.secondary} 
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.4
                                }}
                              >
                                Promo Codes: {plan.promo_codes}
                              </Typography>
                            </li>
                            {plan.personal_assistant === 1 && (
                              <li style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                marginBottom: 12 
                              }}>
                                <CheckCircleIcon 
                                  sx={{ 
                                    color: color, 
                                    fontSize: 20, 
                                    mr: 1.5, 
                                    mt: 0.2,
                                    flexShrink: 0
                                  }} 
                                />
                                <Typography 
                                  variant="body2" 
                                  color={theme.palette.text.secondary} 
                                  sx={{ 
                                    fontWeight: 500,
                                    lineHeight: 1.4
                                  }}
                                >
                                  Personal Assistant
                                </Typography>
                              </li>
                            )}
                          </Box>
                        </Box>

                        {/* Action Button */}
                        <Button 
                          variant="contained" 
                          fullWidth
                          sx={{ 
                            mt: 'auto',
                            bgcolor: color, 
                            color: 'white',
                            fontWeight: 700,
                            borderRadius: 2,
                            fontSize: '1rem',
                            py: 1.5,
                            boxShadow: `0 4px 16px ${color}40`,
                            '&:hover': { 
                              bgcolor: color,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${color}60`
                            },
                            transition: 'all 0.3s'
                          }}
                          onClick={() => handlePurchase(plan)}
                          disabled={processingPayment}
                        >
                          {processingPayment ? 'Processing...' : 'Get Started'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" color={theme.palette.text.secondary} sx={{ mb: 2 }}>
            All plans include 24/7 customer support and 30-day money-back guarantee
          </Typography>
          <Button 
            variant="outlined" 
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ 
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 600,
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                bgcolor: `${theme.palette.primary.main}08`
              }
            }}
          >
            Need Help? Contact Us
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const ButtonGroup = ({ selectedBillingCycle, setSelectedBillingCycle, theme }) => {
  const billingCycles = ['monthly', 'quarterly'];
  const cycleNames = {
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
      {billingCycles.map((cycle) => (
        <Button
          key={cycle}
          variant={selectedBillingCycle === cycle ? 'contained' : 'outlined'}
          onClick={() => setSelectedBillingCycle(cycle)}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            py: 0.8,
            borderColor: selectedBillingCycle === cycle ? theme.palette.primary.main : theme.palette.primary.light,
            color: selectedBillingCycle === cycle ? 'white' : theme.palette.primary.main,
            '&:hover': {
              borderColor: selectedBillingCycle === cycle ? theme.palette.primary.dark : theme.palette.primary.light,
              bgcolor: selectedBillingCycle === cycle ? theme.palette.primary.main : theme.palette.primary.light + '10',
            },
            transition: 'all 0.3s',
          }}
        >
          {cycleNames[cycle]}
        </Button>
      ))}
    </Box>
  );
};

export default Purchase;